import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, ConfigProvider, Layout, message, Result, Space, Typography } from 'antd';
import { Content } from 'antd/es/layout/layout';
import Sider from 'antd/es/layout/Sider';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import { useAccountStore, useManagementStore, useUserState } from '../../store';
import { getCompanyFromAccountUniqueId, PUBLIC_VIEW_ID_PATTERN } from '../../utilities/account-ids';
import axios from '../../utilities/axios';
import { FORMAT, LOGS, SUMMARY } from '../../utilities/functions';
import NotFoundRedirect from '../not-found';

import ContentHeader from './components/content-header';
import Header from './components/header';
import Sidebar from './components/sidebar';
import SubHeader from './components/subheader';

const { Title, Text } = Typography;
// import AddAccount from "./components/add-account";

message.config({
    duration: 10
});

// Custom hook to determine if ContentHeader should be shown
const useShowContentHeader = () => {
    const { pathname } = useLocation();
    // Define paths where ContentHeader should not be shown
    const hiddenPaths = ['/add-trade', '/settings'];
    return !hiddenPaths.some((path) => pathname.startsWith(path));
};

// Custom hook to determine if Subheader should be shown
const useShowSubheader = () => {
    const { pathname } = useLocation();
    // Define paths where ContentHeader should not be shown
    const hiddenPaths = ['/add-trade', '/settings'];
    return !hiddenPaths.some((path) => pathname.startsWith(path));
};

function LayoutMain() {
    const { id, unassignedId } = useParams();
    const [, contextHolder] = message.useMessage();
    const normalizedPublicId = typeof id === 'string' ? id.toUpperCase() : '';
    const normalizedUnassignedId =
        typeof unassignedId === 'string' ? unassignedId.toUpperCase() : '';
    const isPublicRouteCandidate =
        Boolean(normalizedPublicId) && PUBLIC_VIEW_ID_PATTERN.test(normalizedPublicId);
    const isUnassignedRouteCandidate =
        Boolean(normalizedUnassignedId) && PUBLIC_VIEW_ID_PATTERN.test(normalizedUnassignedId);

    // LOADING ALL ACCOUNTS AND CURRENT SELECTED ACCOUNT
    const { allAccounts, setAllAccounts } = useAccountStore((state) => state);
    const { getUser } = useUserState();
    const { viewAs, setViewAs, company, setCompany, setPublicView, publicView } =
        useManagementStore((state) => state);
    const { pathname } = useLocation(); // Import useLocation from react-router-dom
    const isSettingsRoute = pathname.startsWith('/settings');
    const isAdminUnassignedRoute = pathname.startsWith('/admin/u/');
    const isAdminRoute = pathname.startsWith('/admin') && !isAdminUnassignedRoute;
    const [unassignedViewErrorCode, setUnassignedViewErrorCode] = useState(null);
    const [initialLoad, setInitialLoad] = useState(isPublicRouteCandidate);
    const [isPublicIdentityResolved, setIsPublicIdentityResolved] =
        useState(!isPublicRouteCandidate);
    const lastPublicIdRef = useRef(null);
    const isPublicMode = isPublicRouteCandidate || publicView.isActive;
    const isLayoutBlocked = initialLoad || (isPublicRouteCandidate && !isPublicIdentityResolved);
    const inferredUnassignedCompany = getCompanyFromAccountUniqueId(normalizedUnassignedId);
    const effectiveUnassignedCompany =
        isUnassignedRouteCandidate && inferredUnassignedCompany
            ? inferredUnassignedCompany
            : company;

    // Start logic used for Public View
    const { data: userData, error: userDataError } = useQuery(
        ['user-data', normalizedPublicId],
        () => SUMMARY.getUserByPCTId(normalizedPublicId),
        {
            enabled: isPublicRouteCandidate,
            retry: false // Don't retry on error
        }
    );

    useEffect(() => {
        if (!isPublicRouteCandidate) {
            lastPublicIdRef.current = null;
            if (publicView.isActive || publicView.id !== null) {
                setPublicView(false, null);
            }
            if (!isPublicIdentityResolved) {
                setIsPublicIdentityResolved(true);
            }
            if (initialLoad) {
                setInitialLoad(false);
            }
            return;
        }

        // Enter public mode immediately to prevent private UI flash.
        if (lastPublicIdRef.current !== normalizedPublicId) {
            lastPublicIdRef.current = normalizedPublicId;
            setIsPublicIdentityResolved(false);
            setInitialLoad(true);
        }

        if (!publicView.isActive || publicView.id !== normalizedPublicId) {
            setPublicView(true, normalizedPublicId);
        }
    }, [
        initialLoad,
        isPublicIdentityResolved,
        isPublicRouteCandidate,
        normalizedPublicId,
        publicView.id,
        publicView.isActive,
        setPublicView
    ]);

    useEffect(() => {
        if (!isPublicRouteCandidate) return;

        if (userData) {
            const isPublicViewAsAlreadySet =
                viewAs?.newUser?.token === userData?.token &&
                viewAs?.newUser?.id === userData?.id &&
                (viewAs?.mainUser?.token ?? null) === null &&
                (viewAs?.mainUser?.id ?? null) === null;

            if (!isPublicViewAsAlreadySet) {
                setViewAs(
                    {
                        token: userData?.token,
                        id: userData?.id
                    },
                    {
                        token: null,
                        id: null
                    }
                );
            }
            if (!publicView.isActive || publicView.id !== normalizedPublicId) {
                setPublicView(true, normalizedPublicId);
            }
            if (!isPublicIdentityResolved) {
                setIsPublicIdentityResolved(true);
            }
            if (initialLoad) {
                setInitialLoad(false);
            }
        } else if (userDataError) {
            // Keep public-mode chrome hidden on public route even for private/not-found responses.
            if (!publicView.isActive || publicView.id !== normalizedPublicId) {
                setPublicView(true, normalizedPublicId);
            }
            if (!isPublicIdentityResolved) {
                setIsPublicIdentityResolved(true);
            }
            if (initialLoad) {
                setInitialLoad(false);
            }
        }
    }, [
        initialLoad,
        isPublicIdentityResolved,
        isPublicRouteCandidate,
        normalizedPublicId,
        publicView.id,
        publicView.isActive,
        setPublicView,
        setViewAs,
        userData,
        userDataError,
        viewAs?.mainUser?.id,
        viewAs?.mainUser?.token,
        viewAs?.newUser?.id,
        viewAs?.newUser?.token
    ]);
    // End logic used for Public View

    const user = getUser();
    const navigate = useNavigate();

    const isViewAsActive =
        JSON.stringify(viewAs?.newUser?.token || null) !==
        JSON.stringify(viewAs?.mainUser?.token || null);

    useEffect(() => {
        if (!isUnassignedRouteCandidate) return;
        if (!isViewAsActive) return;

        const isViewingAsAdminAlready =
            viewAs?.newUser?.token === user?.token &&
            viewAs?.newUser?.id === user?.id &&
            viewAs?.mainUser?.token === user?.token &&
            viewAs?.mainUser?.id === user?.id;

        if (isViewingAsAdminAlready) return;

        setViewAs({ token: user?.token, id: user?.id }, { token: user?.token, id: user?.id });
    }, [
        isUnassignedRouteCandidate,
        isViewAsActive,
        setViewAs,
        user?.id,
        user?.token,
        viewAs?.mainUser?.id,
        viewAs?.mainUser?.token,
        viewAs?.newUser?.id,
        viewAs?.newUser?.token
    ]);

    useEffect(() => {
        if (!isUnassignedRouteCandidate) return;
        if (!inferredUnassignedCompany) return;
        if (company === inferredUnassignedCompany) return;

        setCompany(inferredUnassignedCompany);
    }, [
        company,
        isUnassignedRouteCandidate,
        inferredUnassignedCompany,
        normalizedUnassignedId,
        setCompany
    ]);

    let authToken;

    if (isPublicMode) {
        authToken = viewAs?.newUser?.token || userData?.token;
    } else if (isUnassignedRouteCandidate) {
        authToken = user?.token;
    } else if (isViewAsActive) {
        authToken = viewAs?.newUser?.token;
    } else {
        authToken = user?.token;
    }

    const fetchAccounts = (token) =>
        axios
            .get('crud/account', {
                headers: { token }
            })
            .then(({ data }) => data.map((a) => FORMAT.ACCOUNTS_BEAUTIFY(a)));
    const fetchUnassignedAccounts = useCallback(
        (token, accountUniqueId) =>
            axios
                .get(`crud/unassigned-live-accounts/${accountUniqueId}/accounts`, {
                    headers: { token },
                    params: { company: effectiveUnassignedCompany }
                })
                .then(({ data }) => data.map((a) => FORMAT.ACCOUNTS_BEAUTIFY(a))),
        [effectiveUnassignedCompany]
    );

    // Fetch and process accounts when:
    // 1. Public route identity is resolved
    // 2. A usable auth token exists for the current mode
    useEffect(() => {
        const fetchAndProcessAccounts = async () => {
            try {
                setUnassignedViewErrorCode(null);
                const accounts = isUnassignedRouteCandidate
                    ? await fetchUnassignedAccounts(authToken, normalizedUnassignedId)
                    : await fetchAccounts(authToken);
                await LOGS.processLogs(accounts, setAllAccounts);
            } catch (error) {
                console.error('Failed to fetch/process accounts for layout:', error);
                if (isUnassignedRouteCandidate) {
                    setUnassignedViewErrorCode(error?.response?.status || 500);
                }
                setAllAccounts([]);
            }
        };

        if (isPublicMode && !isPublicIdentityResolved) return;
        if (!authToken) return;

        fetchAndProcessAccounts();
    }, [
        authToken,
        effectiveUnassignedCompany,
        normalizedUnassignedId,
        setAllAccounts,
        viewAs,
        isPublicMode,
        isPublicIdentityResolved,
        isUnassignedRouteCandidate,
        userData?.token,
        fetchUnassignedAccounts
    ]);

    // Check if allAccounts is empty and the route is not /settings
    // Check if any accounts have trade data
    const hasTradeData = allAccounts.some((account) => account.numberOfTrades > 0);

    // Check account types
    const hasSimulatorAccount = allAccounts.some((account) => account.simulator_account);
    const hasNonSimulatorAccounts = allAccounts.some((account) => !account.simulator_account);
    const onlySimulatorAccounts = hasSimulatorAccount && !hasNonSimulatorAccounts;

    useEffect(() => {
        if (user.role === 'admin') return;

        if (pathname === '/' && !isViewAsActive && allAccounts.length > 0) {
            if (hasTradeData) {
                return;
            }

            if (!hasTradeData) {
                if (onlySimulatorAccounts) {
                    navigate('/settings/1');
                } else if (
                    hasSimulatorAccount &&
                    hasNonSimulatorAccounts &&
                    user.role === 'admin'
                ) {
                    navigate('/add-trade');
                }
            }
        }
    }, [
        hasTradeData,
        hasSimulatorAccount,
        hasNonSimulatorAccounts,
        onlySimulatorAccounts,
        pathname,
        isViewAsActive,
        allAccounts.length,
        navigate,
        user.role
    ]);

    const shouldDisplayCreateAccountMessage =
        allAccounts.length === 0 && !isSettingsRoute && !isUnassignedRouteCandidate;

    const shouldShowContentHeader = useShowContentHeader(); // Use the custom hook
    const shouldShowSubheader = useShowSubheader(); // Use the custom hook

    const renderContent = () => {
        if (userDataError?.response?.status === 404 && isPublicRouteCandidate) {
            return <NotFoundRedirect />;
        }

        if (userDataError?.response?.status === 403) {
            return (
                <Result
                    status="403"
                    title="Private Profile"
                    subTitle="This user's profile is private and cannot be viewed."
                    extra={
                        <Button type="primary" onClick={() => navigate('/')}>
                            Go to Dashboard
                        </Button>
                    }
                />
            );
        }

        if (isUnassignedRouteCandidate && unassignedViewErrorCode === 404) {
            return (
                <Result
                    status="404"
                    title="Unassigned Account Not Found"
                    subTitle="The unassigned account identifier could not be resolved."
                    extra={
                        <Button type="primary" onClick={() => navigate('/')}>
                            Go to Dashboard
                        </Button>
                    }
                />
            );
        }

        if (isUnassignedRouteCandidate && unassignedViewErrorCode === 403) {
            return (
                <Result
                    status="403"
                    title="Access Denied"
                    subTitle="Only administrators can view unassigned live-account journals."
                    extra={
                        <Button type="primary" onClick={() => navigate('/')}>
                            Go to Dashboard
                        </Button>
                    }
                />
            );
        }

        if (isUnassignedRouteCandidate && !unassignedViewErrorCode && allAccounts.length === 0) {
            return (
                <Space direction="vertical" className="no-data" wrap>
                    <Title>This unassigned account has no journal data yet.</Title>
                </Space>
            );
        }

        if (shouldDisplayCreateAccountMessage) {
            if (!isViewAsActive) {
                return (
                    <Space direction="vertical" className="no-data" wrap>
                        <Title>Create a trading account to get started.</Title>
                        <Button type="primary" onClick={() => navigate('settings')}>
                            New Trading Account
                        </Button>
                        <Text>
                            After creating a trading account, you will be able to import your trade
                            logs.
                        </Text>
                        <Text>
                            You can create multiple accounts and journal your trades independently.
                            This can be useful if you use multiple brokerages.
                        </Text>
                    </Space>
                );
            }

            return (
                <Space direction="vertical" className="no-data" wrap>
                    <Title>User doesn&apos;t have any trading account yet.</Title>
                </Space>
            );
        }
        return <Outlet />;
    };

    return (
        <ConfigProvider
            theme={{
                token: {
                    borderRadius: 8,
                    fontFamily: "'Inter', sans-serif"
                },
                components: {
                    Menu: {
                        itemSelectedBg: '#FFFFFF',
                        itemSelectedColor: '#2962FF',
                        itemColor: '#424242'
                    },
                    Pagination: {
                        itemActiveBg: '#455A64',
                        colorPrimary: '#E0E3EB',
                        colorPrimaryHover: '#fafafa'
                    }
                }
            }}
        >
            <Layout>
                {contextHolder}
                <Layout className="app">
                    {!isPublicMode && <Header />}
                    {!isLayoutBlocked && shouldShowSubheader && !isAdminRoute && <SubHeader />}
                    <Layout>
                        {
                            <Sider className="custom-sider">
                                <Sidebar
                                    forcedPublicViewId={
                                        isPublicRouteCandidate ? normalizedPublicId : null
                                    }
                                    forcedUnassignedViewId={
                                        isUnassignedRouteCandidate ? normalizedUnassignedId : null
                                    }
                                />
                            </Sider>
                        }
                        <Layout>
                            {!isPublicMode && shouldShowContentHeader && !isAdminRoute && (
                                <ContentHeader />
                            )}
                            <Content className="content">
                                {!isLayoutBlocked && renderContent()}
                            </Content>
                        </Layout>
                    </Layout>
                </Layout>
            </Layout>
        </ConfigProvider>
    );
}

export default LayoutMain;
